import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

import { tr, lbl } from "../lib/i18n";
export default function PermissionsPage() {
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [schema, setSchema] = useState({ modules: [], actions: [] });
  const [perms, setPerms] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [u, s] = await Promise.all([api.get("/users"), api.get("/users/permission-schema")]);
        setUsers(u.data);
        setSchema(s.data);
        if (u.data.length > 0) {
          const first = u.data.find((x) => x.role !== "admin") || u.data[0];
          setSelectedId(first.id);
          setPerms(first.permissions || {});
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const selectUser = (u) => {
    setSelectedId(u.id);
    setPerms(u.permissions || {});
  };

  const toggle = (m, a) => {
    setPerms((prev) => ({
      ...prev,
      [m]: { ...(prev[m] || {}), [a]: !prev[m]?.[a] },
    }));
  };

  const save = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await api.patch(`/users/${selectedId}`, { permissions: perms });
      toast.success(tr("x.permissions_saved"));
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const selected = users.find((u) => u.id === selectedId);

  return (
    <div className="space-y-5" data-testid="permissions-page">
      <div>
        <div className="eyebrow">{tr("x.access_control")}</div>
        <h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>{tr("x.permissions")}</h1>
        <p className="text-sm text-[var(--gray-400)] mt-1">{tr("x.grant_fine_grained_access_per_user_per_m")}</p>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <div className="panel p-3 md:col-span-1">
          <div className="eyebrow mb-2 px-2">{tr("x.users")}</div>
          <ul className="space-y-1 max-h-[600px] overflow-y-auto">
            {users.map((u) => (
              <li key={u.id}>
                <button
                  data-testid={`select-user-${u.email}`}
                  onClick={() => selectUser(u)}
                  className={`w-full text-start px-3 py-2 rounded-lg text-sm ${selectedId === u.id ? "bg-[var(--accent-soft)] text-accent-brand" : "hover:bg-[hsl(var(--muted))]"}`}
                >
                  <div className="font-semibold">{u.full_name}</div>
                  <div className="text-xs text-[var(--gray-400)]"><bdi>{u.email}</bdi> · {lbl(u.role)}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="panel p-5 md:col-span-3 overflow-x-auto">
          {selected ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="eyebrow">{tr("x.editing")}</div>
                  <div className="font-semibold text-lg">{selected.full_name}</div>
                  <div className="text-xs text-[var(--gray-400)]"><bdi>{selected.email}</bdi> · {lbl(selected.role)}</div>
                </div>
                <button data-testid="save-permissions" onClick={save} disabled={busy || selected.role === "admin"} className="h-9 px-4 rounded-lg bg-accent-brand text-white text-sm font-semibold disabled:opacity-40">
                  {selected.role === "admin" ? tr("x.admin_bypass") : busy ? tr("x.saving") : tr("x.save")}
                </button>
              </div>

              <table className="w-full dense-table">
                <thead>
                  <tr>
                    <th>{tr("x.module")}</th>
                    {schema.actions.map((a) => <th key={a} className="text-center">{lbl(a)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {schema.modules.map((m) => (
                    <tr key={m}>
                      <td className="font-medium capitalize">{lbl(m)}</td>
                      {schema.actions.map((a) => (
                        <td key={a} className="text-center">
                          <input
                            type="checkbox"
                            data-testid={`perm-${m}-${a}`}
                            disabled={selected.role === "admin"}
                            checked={!!perms[m]?.[a] || selected.role === "admin"}
                            onChange={() => toggle(m, a)}
                            className="w-4 h-4 accent-[var(--accent)]"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className="text-sm text-[var(--gray-400)]">{tr("x.select_a_user_to_edit_permissions")}</div>
          )}
        </div>
      </div>
    </div>
  );
}
